import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useDispatch, useSelector } from 'react-redux';
import { signUp, selectAuthError, selectAuthLoading } from '../../store/slices/authSlice';
import { AppDispatch } from '../../store';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Container, Box, Typography, TextField, Button, Alert, Link, CircularProgress, Divider } from '@mui/material';
import GoogleAuthButton from '../../components/GoogleAuthButton';

const signupSchema = z.object({
    name: z.string().min(1, { message: 'Name is required' }),
    email: z.string().email({ message: 'Invalid email address' }),
    password: z.string().min(8, { message: 'Password must be at least 8 characters' }),
    password_confirmation: z.string(),
}).refine((data) => data.password === data.password_confirmation, {
    message: "Passwords don't match",
    path: ["password_confirmation"],
});

type SignupFormValues = z.infer<typeof signupSchema>;

const SignupPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const authError = useSelector(selectAuthError);
    const isLoading = useSelector(selectAuthLoading);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<SignupFormValues>({
        resolver: zodResolver(signupSchema),
    });

    const onSubmit = async (data: SignupFormValues) => {
        const result = await dispatch(signUp(data));
        if (signUp.fulfilled.match(result)) {
            // Auto-login after successful signup, redirect to home
            navigate('/');
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Box
                    component="img"
                    src="/img/logo.png"
                    alt="Smart Nyuki Logo"
                    sx={{
                        width: 120,
                        height: 'auto',
                        mb: 2,
                    }}
                />
                <Typography component="h1" variant="h5">
                    Sign Up
                </Typography>
                <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 1 }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="name"
                        label="Name"
                        autoFocus
                        {...register('name')}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email Address"
                        autoComplete="email"
                        {...register('email')}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        label="Password"
                        type="password"
                        id="password"
                        autoComplete="new-password"
                        {...register('password')}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        label="Confirm Password"
                        type="password"
                        id="password_confirmation"
                        {...register('password_confirmation')}
                        error={!!errors.password_confirmation}
                        helperText={errors.password_confirmation?.message}
                    />
                    {authError && <Alert severity="error" sx={{ width: '100%', mt: 2 }}>{authError}</Alert>}
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={isLoading}
                    >
                        {isLoading ? <CircularProgress size={24} /> : 'Sign Up'}
                    </Button>
                    <Divider sx={{ my: 2 }}>OR</Divider>
                    <GoogleAuthButton
                        onSuccess={() => navigate('/')}
                        onError={(err) => console.error(err)}
                    />
                    <Link component={RouterLink} to="/auth/sign-in" variant="body2">
                        {"Already have an account? Login"}
                    </Link>
                </Box>
            </Box>
        </Container>
    );
};

export default SignupPage;
